import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../../ui/toast';
import {
  createBankSearch,
  deleteBankSearch,
  getBankSearchResults,
  listBankSearches,
  patchBankSearch,
  refreshBankSearch,
} from '../../../utils/api_service/candi_bank_search';
import {
  BankSearch,
  BankSearchPool,
  BankSearchResult,
  BankSearchResults,
  BankSearchWeights,
  PatchBankSearch,
  ResultsOverride,
} from '../../../utils/types/candi_bank_types';
import { useBankSearchPoller } from './useBankSearchPoller';

export interface UseBankSearchReturn {
  // Search list
  searches: BankSearch[];
  searchListTotal: number;
  searchListPage: number;
  searchListLoading: boolean;
  setSearchListPage: (page: number) => void;
  refreshSearchList: () => void;

  // Active search
  activeSearch: BankSearch | null;
  setActiveSearch: (search: BankSearch | null) => void;
  selectSearch: (search: BankSearch) => void;

  // Results
  results: BankSearchResult[];
  unverifiedResults: BankSearchResult[];
  pool: BankSearchPool | null;
  resultsTotal: number;
  resultsPage: number;
  resultsLoading: boolean;
  setResultsPage: (page: number) => void;
  showUnverified: boolean;
  setShowUnverified: (show: boolean) => void;

  // Create
  showCreateModal: boolean;
  setShowCreateModal: (show: boolean) => void;
  createFromFile: (file: File) => Promise<void>;
  createFromText: (text: string) => Promise<void>;
  createFromOpening: (jobOpeningId: string) => Promise<void>;
  createLoading: boolean;

  // Polling / processing
  isProcessing: boolean;
  pollerState: string;
  processingError: string | null;
  retrySearch: () => Promise<void>;

  // Edits
  patchSkills: (
    requiredSkills: string[],
    mandatorySkills: string[],
    desirableSkills: string[],
  ) => Promise<void>;
  patchJobTitle: (jobTitle: string) => Promise<void>;
  patchEducations: (educations: string[]) => Promise<void>;
  patchCertifications: (certifications: string[]) => Promise<void>;
  patchRemoveMatches: (
    skill: string,
    matches: string[],
  ) => Promise<void>;
  patchExperience: (
    minimumWorkExperience: number,
    requireExperience: boolean,
  ) => Promise<void>;
  patchTitle: (title: string) => Promise<void>;
  isReindexing: boolean;

  // Weights
  weightOverride: Partial<BankSearchWeights> | null;
  setWeightOverride: (weights: Partial<BankSearchWeights> | null) => void;
  commitWeights: () => Promise<void>;
  resetWeights: () => void;
  applyWeightOverride: (override: ResultsOverride) => void;

  // Actions
  handleRefreshFromOpening: () => Promise<void>;
  handleDelete: (searchId: string) => Promise<void>;

  // Stale matches
  skillMatchesStale: boolean;
}

const RESULTS_LIMIT = 20;
const SEARCH_LIST_LIMIT = 20;

export const useBankSearch = (): UseBankSearchReturn => {
  // ── Search list ──────────────────────────────────────────────────────
  const [searches, setSearches] = useState<BankSearch[]>([]);
  const [searchListTotal, setSearchListTotal] = useState(0);
  const [searchListPage, setSearchListPage] = useState(1);
  const [searchListLoading, setSearchListLoading] = useState(false);

  // ── Active search ────────────────────────────────────────────────────
  const [activeSearch, setActiveSearch] = useState<BankSearch | null>(null);

  // ── Results ──────────────────────────────────────────────────────────
  const [results, setResults] = useState<BankSearchResult[]>([]);
  const [unverifiedResults, setUnverifiedResults] = useState<BankSearchResult[]>([]);
  const [pool, setPool] = useState<BankSearchPool | null>(null);
  const [resultsTotal, setResultsTotal] = useState(0);
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showUnverified, setShowUnverified] = useState(false);

  // ── Create ───────────────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // ── Processing ───────────────────────────────────────────────────────
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [isReindexing, setIsReindexing] = useState(false);

  // ── Weights ──────────────────────────────────────────────────────────
  const [weightOverride, setWeightOverride] = useState<Partial<BankSearchWeights> | null>(null);

  // ── Stale matches ────────────────────────────────────────────────────
  const [skillMatchesStale, setSkillMatchesStale] = useState(false);

  // Track the last create input for retry
  const lastCreateInputRef = useRef<
    { file: File } | { text: string } | { job_opening_id: string } | null
  >(null);

  // ── Poller ───────────────────────────────────────────────────────────
  const handlePollComplete = useCallback(
    (search: BankSearch) => {
      setActiveSearch(search);
      setIsProcessing(false);
      setIsReindexing(false);
      setProcessingError(null);
      // Auto-fetch results when processing completes
      fetchResults(search.id, 1, false);
      refreshSearchList();
    },
    [],
  );

  const handlePollError = useCallback((search: BankSearch) => {
    setActiveSearch(search);
    setIsProcessing(false);
    setIsReindexing(false);
    setProcessingError(search.errorMessage);
  }, []);

  const {
    pollerState,
    startPolling,
    stopPolling,
  } = useBankSearchPoller(handlePollComplete, handlePollError);

  // ── Fetch search list ────────────────────────────────────────────────
  const fetchSearchList = useCallback(async (page: number = 1) => {
    setSearchListLoading(true);
    try {
      const response = await listBankSearches(page, SEARCH_LIST_LIMIT);
      if (response.ok) {
        const data = await response.json();
        setSearches(data.searches);
        setSearchListTotal(data.total);
      }
    } catch {
      toast.error('Failed to load searches', { position: 'bottom-center' });
    } finally {
      setSearchListLoading(false);
    }
  }, []);

  const refreshSearchList = useCallback(() => {
    fetchSearchList(searchListPage);
  }, [fetchSearchList, searchListPage]);

  // Initial load
  useEffect(() => {
    fetchSearchList(searchListPage);
  }, [searchListPage, fetchSearchList]);

  // ── Fetch results ────────────────────────────────────────────────────
  const fetchResults = useCallback(
    async (
      searchId: string,
      page: number = 1,
      includeUnverified: boolean = false,
      override?: ResultsOverride,
    ) => {
      setResultsLoading(true);
      try {
        const response = await getBankSearchResults(
          searchId,
          page,
          RESULTS_LIMIT,
          includeUnverified,
          override,
        );

        if (response.status === 409) {
          // Not ready yet — search not completed
          setResultsLoading(false);
          return;
        }

        if (response.ok) {
          const data: BankSearchResults = await response.json();
          setResults(data.results);
          setUnverifiedResults(data.unverifiedResults ?? []);
          setPool(data.pool);
          setResultsTotal(data.total);
          setResultsPage(data.page);
          setSkillMatchesStale(data.pool.skillMatchesStale);
          // Update the search object from results too
          setActiveSearch(data.search);
        }
      } catch {
        toast.error('Failed to load results', { position: 'bottom-center' });
      } finally {
        setResultsLoading(false);
      }
    },
    [],
  );

  // Re-fetch results when page or unverified toggle changes
  useEffect(() => {
    if (activeSearch?.completed) {
      fetchResults(activeSearch.id, resultsPage, showUnverified);
    }
  }, [resultsPage, showUnverified]);

  // ── Stale matches auto-refresh ───────────────────────────────────────
  useEffect(() => {
    if (!skillMatchesStale || !activeSearch?.completed) return;
    const timer = setTimeout(() => {
      fetchResults(activeSearch.id, resultsPage, showUnverified);
    }, 5000);
    return () => clearTimeout(timer);
  }, [skillMatchesStale, activeSearch, resultsPage, showUnverified, fetchResults]);

  // ── Select a search ──────────────────────────────────────────────────
  const selectSearch = useCallback(
    (search: BankSearch) => {
      stopPolling();
      setActiveSearch(search);
      setProcessingError(null);
      setResults([]);
      setUnverifiedResults([]);
      setPool(null);
      setResultsTotal(0);
      setResultsPage(1);
      setWeightOverride(null);
      setIsReindexing(false);
      setShowUnverified(false);

      if (search.status === 'Completed' && search.completed) {
        fetchResults(search.id, 1, false);
      } else if (search.status === 'Error') {
        setProcessingError(search.errorMessage);
      } else {
        // In Queue or Processing — start polling
        setIsProcessing(true);
        startPolling(search.id);
      }
    },
    [stopPolling, startPolling, fetchResults],
  );

  // ── Create search ────────────────────────────────────────────────────
  const handleCreateResponse = useCallback(
    async (response: Response) => {
      const data = await response.json();
      const search: BankSearch = data.search;

      setActiveSearch(search);
      setShowCreateModal(false);
      refreshSearchList();

      if (response.status === 202 || data.queued) {
        // Queued for processing
        setIsProcessing(true);
        startPolling(search.id);
      } else if (search.completed) {
        // Deduped — already completed
        toast.info('You already have a search for this JD', {
          position: 'bottom-center',
        });
        fetchResults(search.id, 1, false);
      } else if (search.status === 'Error') {
        setProcessingError(search.errorMessage);
      } else {
        // Still processing (deduped but not done yet)
        setIsProcessing(true);
        startPolling(search.id);
      }
    },
    [startPolling, fetchResults, refreshSearchList],
  );

  const createFromFile = useCallback(
    async (file: File) => {
      setCreateLoading(true);
      lastCreateInputRef.current = { file };
      try {
        const response = await createBankSearch({ file });
        if (!response.ok && response.status !== 202) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `Error ${response.status}`);
        }
        await handleCreateResponse(response);
      } catch (e: any) {
        toast.error(e.message || 'Failed to create search', {
          position: 'bottom-center',
        });
      } finally {
        setCreateLoading(false);
      }
    },
    [handleCreateResponse],
  );

  const createFromText = useCallback(
    async (text: string) => {
      setCreateLoading(true);
      lastCreateInputRef.current = { text };
      try {
        const response = await createBankSearch({ text });
        if (!response.ok && response.status !== 202) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `Error ${response.status}`);
        }
        await handleCreateResponse(response);
      } catch (e: any) {
        toast.error(e.message || 'Failed to create search', {
          position: 'bottom-center',
        });
      } finally {
        setCreateLoading(false);
      }
    },
    [handleCreateResponse],
  );

  const createFromOpening = useCallback(
    async (jobOpeningId: string) => {
      setCreateLoading(true);
      lastCreateInputRef.current = { job_opening_id: jobOpeningId };
      try {
        const response = await createBankSearch({ job_opening_id: jobOpeningId });
        if (!response.ok && response.status !== 202) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `Error ${response.status}`);
        }
        await handleCreateResponse(response);
      } catch (e: any) {
        toast.error(e.message || 'Failed to create search', {
          position: 'bottom-center',
        });
      } finally {
        setCreateLoading(false);
      }
    },
    [handleCreateResponse],
  );

  // ── Retry (re-POST same input after Error) ───────────────────────────
  const retrySearch = useCallback(async () => {
    if (!lastCreateInputRef.current && activeSearch) {
      // If we don't have the original input, re-POST based on source
      if (activeSearch.source === 'opening' && activeSearch.jobOpeningId) {
        await createFromOpening(activeSearch.jobOpeningId);
      } else {
        toast.error('Cannot retry — original input not available', {
          position: 'bottom-center',
        });
      }
      return;
    }

    if (!lastCreateInputRef.current) return;

    const input = lastCreateInputRef.current;
    if ('file' in input) {
      await createFromFile(input.file);
    } else if ('text' in input) {
      await createFromText(input.text);
    } else {
      await createFromOpening(input.job_opening_id);
    }
  }, [activeSearch, createFromFile, createFromText, createFromOpening]);

  // ── Helper: handle PATCH response (202 vs 200) ──────────────────────
  const handlePatchResponse = useCallback(
    async (response: Response) => {
      const data = await response.json();
      const search: BankSearch = data.search;
      setActiveSearch(search);

      if (response.status === 202 || data.queued) {
        // Re-indexing needed — poll
        setIsReindexing(true);
        startPolling(search.id);
      } else {
        // Effective immediately — re-fetch results
        if (search.completed) {
          fetchResults(search.id, resultsPage, showUnverified);
        }
      }
      refreshSearchList();
    },
    [startPolling, fetchResults, refreshSearchList, resultsPage, showUnverified],
  );

  // ── Patch: skills (re-index path) ────────────────────────────────────
  const patchSkills = useCallback(
    async (
      requiredSkills: string[],
      mandatorySkills: string[],
      desirableSkills: string[],
    ) => {
      if (!activeSearch) return;
      try {
        const response = await patchBankSearch(activeSearch.id, {
          required_skills: requiredSkills,
          mandatory_skills: mandatorySkills,
          desirable_skills: desirableSkills,
        });
        if (response.ok || response.status === 202) {
          await handlePatchResponse(response);
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.error || 'Failed to update skills', {
            position: 'bottom-center',
          });
        }
      } catch {
        toast.error('Failed to update skills', { position: 'bottom-center' });
      }
    },
    [activeSearch, handlePatchResponse],
  );

  // ── Patch: job title (re-index path) ─────────────────────────────────
  const patchJobTitle = useCallback(
    async (jobTitle: string) => {
      if (!activeSearch) return;
      try {
        const response = await patchBankSearch(activeSearch.id, {
          job_title: jobTitle,
        });
        if (response.ok || response.status === 202) {
          await handlePatchResponse(response);
        }
      } catch {
        toast.error('Failed to update job title', { position: 'bottom-center' });
      }
    },
    [activeSearch, handlePatchResponse],
  );

  // ── Patch: educations (re-index path) ────────────────────────────────
  const patchEducations = useCallback(
    async (educations: string[]) => {
      if (!activeSearch) return;
      try {
        const response = await patchBankSearch(activeSearch.id, { educations });
        if (response.ok || response.status === 202) {
          await handlePatchResponse(response);
        }
      } catch {
        toast.error('Failed to update educations', { position: 'bottom-center' });
      }
    },
    [activeSearch, handlePatchResponse],
  );

  // ── Patch: certifications (re-index path) ────────────────────────────
  const patchCertifications = useCallback(
    async (certifications: string[]) => {
      if (!activeSearch) return;
      try {
        const response = await patchBankSearch(activeSearch.id, { certifications });
        if (response.ok || response.status === 202) {
          await handlePatchResponse(response);
        }
      } catch {
        toast.error('Failed to update certifications', {
          position: 'bottom-center',
        });
      }
    },
    [activeSearch, handlePatchResponse],
  );

  // ── Patch: remove matches (instant) ──────────────────────────────────
  const patchRemoveMatches = useCallback(
    async (skill: string, matches: string[]) => {
      if (!activeSearch) return;
      try {
        const response = await patchBankSearch(activeSearch.id, {
          remove_matches: [{ skill, matches }],
        });
        if (response.ok) {
          const data = await response.json();
          setActiveSearch(data.search);
          // Re-fetch results to reflect the change
          if (data.search.completed) {
            fetchResults(data.search.id, resultsPage, showUnverified);
          }
        }
      } catch {
        toast.error('Failed to remove match', { position: 'bottom-center' });
      }
    },
    [activeSearch, fetchResults, resultsPage, showUnverified],
  );

  // ── Patch: experience (instant) ──────────────────────────────────────
  const patchExperience = useCallback(
    async (minimumWorkExperience: number, requireExperience: boolean) => {
      if (!activeSearch) return;
      try {
        const response = await patchBankSearch(activeSearch.id, {
          minimum_work_experience: minimumWorkExperience,
          require_experience: requireExperience,
        });
        if (response.ok) {
          const data = await response.json();
          setActiveSearch(data.search);
          if (data.search.completed) {
            fetchResults(data.search.id, resultsPage, showUnverified);
          }
        }
      } catch {
        toast.error('Failed to update experience', { position: 'bottom-center' });
      }
    },
    [activeSearch, fetchResults, resultsPage, showUnverified],
  );

  // ── Patch: title (instant) ───────────────────────────────────────────
  const patchTitle = useCallback(
    async (title: string) => {
      if (!activeSearch) return;
      try {
        const response = await patchBankSearch(activeSearch.id, { title });
        if (response.ok) {
          const data = await response.json();
          setActiveSearch(data.search);
          refreshSearchList();
        }
      } catch {
        toast.error('Failed to rename search', { position: 'bottom-center' });
      }
    },
    [activeSearch, refreshSearchList],
  );

  // ── Weights: live override (no save) ─────────────────────────────────
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const applyWeightOverride = useCallback(
    (override: ResultsOverride) => {
      if (!activeSearch?.completed) return;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        fetchResults(activeSearch.id, resultsPage, showUnverified, override);
      }, 300);
    },
    [activeSearch, fetchResults, resultsPage, showUnverified],
  );

  // ── Weights: commit (persist) ────────────────────────────────────────
  const commitWeights = useCallback(async () => {
    if (!activeSearch || !weightOverride) return;
    try {
      const response = await patchBankSearch(activeSearch.id, {
        weights: {
          semantic: weightOverride.semantic,
          mandatory_skills: weightOverride.mandatorySkills,
          desirable_skills: weightOverride.desirableSkills,
          experience: weightOverride.experience,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setActiveSearch(data.search);
        setWeightOverride(null);
        toast.success('Weights saved', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Failed to save weights', { position: 'bottom-center' });
    }
  }, [activeSearch, weightOverride]);

  const resetWeights = useCallback(() => {
    setWeightOverride(null);
    if (activeSearch?.completed) {
      fetchResults(activeSearch.id, resultsPage, showUnverified);
    }
  }, [activeSearch, fetchResults, resultsPage, showUnverified]);

  // ── Refresh from opening ─────────────────────────────────────────────
  const handleRefreshFromOpening = useCallback(async () => {
    if (!activeSearch) return;
    try {
      const response = await refreshBankSearch(activeSearch.id);
      if (response.ok || response.status === 202) {
        setIsProcessing(true);
        startPolling(activeSearch.id);
        toast.info('Refreshing from opening — your edits will be discarded', {
          position: 'bottom-center',
        });
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || 'Failed to refresh', {
          position: 'bottom-center',
        });
      }
    } catch {
      toast.error('Failed to refresh from opening', {
        position: 'bottom-center',
      });
    }
  }, [activeSearch, startPolling]);

  // ── Delete ───────────────────────────────────────────────────────────
  const handleDelete = useCallback(
    async (searchId: string) => {
      try {
        const response = await deleteBankSearch(searchId);
        if (response.ok) {
          if (activeSearch?.id === searchId) {
            setActiveSearch(null);
            setResults([]);
            setPool(null);
            stopPolling();
          }
          refreshSearchList();
          toast.success('Search deleted', { position: 'bottom-center' });
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.error || 'Failed to delete', {
            position: 'bottom-center',
          });
        }
      } catch {
        toast.error('Failed to delete search', { position: 'bottom-center' });
      }
    },
    [activeSearch, stopPolling, refreshSearchList],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return {
    // Search list
    searches,
    searchListTotal,
    searchListPage,
    searchListLoading,
    setSearchListPage,
    refreshSearchList,

    // Active search
    activeSearch,
    setActiveSearch,
    selectSearch,

    // Results
    results,
    unverifiedResults,
    pool,
    resultsTotal,
    resultsPage,
    resultsLoading,
    setResultsPage,
    showUnverified,
    setShowUnverified,

    // Create
    showCreateModal,
    setShowCreateModal,
    createFromFile,
    createFromText,
    createFromOpening,
    createLoading,

    // Polling / processing
    isProcessing,
    pollerState,
    processingError,
    retrySearch,

    // Edits
    patchSkills,
    patchJobTitle,
    patchEducations,
    patchCertifications,
    patchRemoveMatches,
    patchExperience,
    patchTitle,
    isReindexing,

    // Weights
    weightOverride,
    setWeightOverride,
    commitWeights,
    resetWeights,
    applyWeightOverride,

    // Actions
    handleRefreshFromOpening,
    handleDelete,

    // Stale matches
    skillMatchesStale,
  };
};
